/**
 * Shared UI kit: buttons, cards, badges, form fields, loading states, icons.
 * Every route composes these so the app reads as one system on phone and laptop.
 */
import { forwardRef } from "react";
import type {
  ButtonHTMLAttributes,
  CSSProperties,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { Link, type LinkProps } from "react-router-dom";
import { cx } from "../lib/cx";
import { loginPath, signupPath } from "../lib/nextParam";
import type { Tournament } from "../lib/types";

/** Re-exported so routes keep importing their class helper from the ui kit. */
export { cx };

/** Staggered entrance delay for lists rendered with `animate-rise`. */
export function stagger(index: number, stepMs = 50): CSSProperties {
  return { animationDelay: `${Math.min(index, 12) * stepMs}ms` };
}

/* ---------------------------------- buttons --------------------------------- */

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "onDark";
type ButtonSize = "sm" | "md" | "lg" | "xl";

const buttonBase =
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg font-medium transition-all duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 active:scale-[.97] disabled:pointer-events-none disabled:opacity-50";

const buttonVariants: Record<ButtonVariant, string> = {
  primary: "bg-brand-600 text-brandFg shadow-sm shadow-brand-600/20 hover:bg-brand-700",
  secondary:
    "border border-slate-300 bg-white text-slate-700 shadow-sm hover:border-slate-400 hover:text-slate-900",
  ghost: "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
  danger: "border border-red-200 bg-white text-red-600 shadow-sm hover:border-red-300 hover:bg-red-50",
  onDark: "bg-white/10 text-white ring-1 ring-inset ring-white/20 hover:bg-white/20",
};

const buttonSizes: Record<ButtonSize, string> = {
  sm: "px-2.5 py-1.5 text-xs",
  md: "px-4 py-2 text-sm",
  lg: "px-5 py-2.5 text-sm",
  // The one button a scorer presses with a thumb at the field: Final on a
  // score card (console spec §5.5).
  xl: "px-6 py-3 text-base",
};

export function buttonCls(
  variant: ButtonVariant = "primary",
  size: ButtonSize = "md",
  className?: string,
): string {
  return cx(buttonBase, buttonVariants[variant], buttonSizes[size], className);
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

export function Button({ variant = "primary", size = "md", className, ...rest }: ButtonProps) {
  return <button className={buttonCls(variant, size, className)} {...rest} />;
}

interface ButtonLinkProps extends LinkProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
}

export function ButtonLink({ variant = "primary", size = "md", className, ...rest }: ButtonLinkProps) {
  return <Link className={buttonCls(variant, size, className)} {...rest} />;
}

/* ------------------------------- form controls ------------------------------ */

const fieldBase =
  "w-full rounded-lg border border-slate-300 bg-white text-slate-800 shadow-sm transition-[border-color,box-shadow] duration-150 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20";

const fieldSizes = {
  sm: "px-2.5 py-1.5 text-xs",
  md: "px-3 py-2 text-sm",
} as const;

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  uiSize?: keyof typeof fieldSizes;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { uiSize = "md", className, ...rest },
  ref,
) {
  return <input ref={ref} className={cx(fieldBase, fieldSizes[uiSize], className)} {...rest} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, rows = 4, ...rest }, ref) {
    return (
      <textarea
        ref={ref}
        rows={rows}
        className={cx(fieldBase, fieldSizes.md, "resize-y leading-relaxed", className)}
        {...rest}
      />
    );
  },
);

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  uiSize?: keyof typeof fieldSizes;
}

/** Native select with a custom chevron; pass width via className (wrapper). */
export function Select({ uiSize = "md", className, children, ...rest }: SelectProps) {
  return (
    <div className={cx("relative", className)}>
      <select className={cx(fieldBase, fieldSizes[uiSize], "appearance-none pr-8")} {...rest}>
        {children}
      </select>
      <svg
        className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </div>
  );
}

export function Field({
  label,
  hint,
  className,
  children,
}: {
  label: string;
  hint?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={cx("block text-sm", className)}>
      <span className="mb-1.5 block font-medium text-slate-600">{label}</span>
      {children}
      {hint ? <span className="mt-1 block text-xs text-slate-400">{hint}</span> : null}
    </label>
  );
}

/* ----------------------------------- cards ---------------------------------- */

export function Card({
  className,
  hover = false,
  style,
  children,
}: {
  className?: string;
  hover?: boolean;
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <div
      style={style}
      className={cx(
        "rounded-2xl border border-slate-200/80 bg-white shadow-card",
        hover &&
          "transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-card-hover",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function SectionCard({
  title,
  description,
  badge,
  actions,
  className,
  style,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  badge?: ReactNode;
  actions?: ReactNode;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <Card className={cx("p-5 sm:p-6", className)} style={style}>
      <div className="mb-4 space-y-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <h2 className="font-display text-lg font-semibold tracking-tight text-navy-950">{title}</h2>
            {badge}
          </div>
          {actions}
        </div>
        {description ? <p className="text-sm text-slate-500">{description}</p> : null}
      </div>
      <div className="space-y-4">{children}</div>
    </Card>
  );
}

export function Section({
  title,
  badge,
  actions,
  id,
  className,
  style,
  children,
}: {
  title: ReactNode;
  badge?: ReactNode;
  actions?: ReactNode;
  id?: string;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <section id={id} className={cx("space-y-3", className)} style={style}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <h2 className="font-display text-lg font-semibold tracking-tight text-navy-950">{title}</h2>
          {badge}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

/**
 * Four screens are the same screen: teams, tournaments, venues, and admins each
 * show a collection and a form that adds to it. Stacked, the form sits below
 * the fold on a wide monitor while the list leaves half the width empty.
 *
 * Below xl they stay stacked, list first, because that is the order the work
 * happens in and the order a screen reader should hear. At xl the form moves
 * alongside as a sticky rail: the collection and the way to extend it are both
 * on screen, and adding three venues no longer means scrolling three times.
 *
 * `items-start` matters — without it the two columns stretch to equal height
 * and the rail's card grows a tall empty tail next to a long list.
 */
export function Workbench({ aside, children }: { aside: ReactNode; children: ReactNode }) {
  return (
    <div className="grid items-start gap-8 xl:grid-cols-[minmax(0,2fr)_minmax(20rem,1fr)]">
      <div className="min-w-0 space-y-8">{children}</div>
      {/* top-24 clears the sticky 4rem header plus the page's top padding. */}
      <div className="min-w-0 xl:sticky xl:top-24">{aside}</div>
    </div>
  );
}

export function PageHeader({
  eyebrow,
  title,
  subtitle,
  actions,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex animate-rise flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0 space-y-1">
        {eyebrow ? (
          <div className="text-xs font-semibold uppercase tracking-wider text-brandText">{eyebrow}</div>
        ) : null}
        <h1 className="font-display text-2xl font-bold tracking-tight text-navy-950 sm:text-3xl">
          {title}
        </h1>
        {subtitle ? <div className="text-sm text-slate-500">{subtitle}</div> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/* ---------------------------------- badges ---------------------------------- */

export type BadgeTone = "slate" | "green" | "amber" | "red" | "brand" | "navy";

const badgeTones: Record<BadgeTone, string> = {
  slate: "bg-slate-100 text-slate-600 ring-slate-500/10",
  green: "bg-brand-50 text-brandText ring-brand-600/20",
  amber: "bg-amber-50 text-amber-700 ring-amber-600/20",
  red: "bg-red-50 text-red-700 ring-red-600/15",
  brand: "bg-brand-600 text-brandFg ring-brand-600",
  navy: "bg-navy-900 text-white ring-navy-900",
};

export function Badge({
  tone = "slate",
  pulse = false,
  className,
  children,
}: {
  tone?: BadgeTone;
  pulse?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ring-1 ring-inset",
        badgeTones[tone],
        className,
      )}
    >
      {pulse ? (
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-60" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-current" />
        </span>
      ) : null}
      {children}
    </span>
  );
}

export function RegStatusBadge({ status }: { status: "pending" | "approved" | "rejected" }) {
  const map = {
    pending: { tone: "amber", label: "Pending" },
    approved: { tone: "green", label: "Approved" },
    rejected: { tone: "red", label: "Rejected" },
  } as const;
  const m = map[status];
  return (
    <Badge tone={m.tone} pulse={status === "pending"}>
      {m.label}
    </Badge>
  );
}

export function TournamentStatusBadge({ status }: { status: Tournament["status"] }) {
  const map = {
    draft: { tone: "slate", label: "Draft" },
    published: { tone: "green", label: "Published" },
    complete: { tone: "navy", label: "Complete" },
    archived: { tone: "amber", label: "Archived" },
  } as const;
  const m = map[status];
  return <Badge tone={m.tone}>{m.label}</Badge>;
}

export function GameStatusBadge({
  status,
  live = false,
  hideScheduled = false,
}: {
  status: "scheduled" | "final" | "forfeit" | "canceled";
  /**
   * The game's scheduled window contains the current time. Honoured only for a
   * `scheduled` game: `live` is derived from a clock and can lag a posted score
   * by up to a tick, so a result that exists always wins.
   */
  live?: boolean;
  /**
   * Drop the plain "Scheduled" pill, for a caller whose layout already says
   * when the game starts. It no longer means "hide every scheduled game": a
   * live one is checked first and still shows. That order is load-bearing, not
   * incidental — the public schedule passes `hideScheduled` on every card, so
   * flipping the two guards would hide the Live badge everywhere it matters.
   */
  hideScheduled?: boolean;
}) {
  if (status === "scheduled" && live)
    return (
      <Badge tone="red" pulse>
        Live
      </Badge>
    );
  if (status === "scheduled" && hideScheduled) return null;
  const map = {
    scheduled: { tone: "slate", label: "Scheduled" },
    final: { tone: "navy", label: "Final" },
    forfeit: { tone: "amber", label: "Forfeit" },
    canceled: { tone: "red", label: "Canceled" },
  } as const;
  const m = map[status];
  return <Badge tone={m.tone}>{m.label}</Badge>;
}

/* ------------------------------ loading & empty ------------------------------ */

export function Spinner({ className }: { className?: string }) {
  return (
    <svg className={cx("animate-spin", className ?? "h-5 w-5")} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
      <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function LoadingBlock({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="flex animate-fade-in flex-col items-center justify-center gap-3 py-20 text-slate-400">
      <Spinner className="h-6 w-6 text-brand-600" />
      <p className="text-sm">{label}</p>
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cx(
        "animate-shimmer rounded-lg bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 bg-[length:200%_100%]",
        className,
      )}
      aria-hidden
    />
  );
}

export function EmptyState({
  icon,
  title,
  hint,
  action,
  className,
}: {
  icon?: ReactNode;
  title: string;
  hint?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cx(
        "flex animate-fade-in flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-white/60 px-6 py-12 text-center",
        className,
      )}
    >
      {icon ? (
        <div className="mb-1 flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-400">
          {icon}
        </div>
      ) : null}
      <p className="text-sm font-medium text-slate-600">{title}</p>
      {hint ? <p className="max-w-sm text-sm text-slate-400">{hint}</p> : null}
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  );
}

/** The gate on a signed-out protected page. `next` brings the user back here. */
export function SignInCard({
  title = "Sign in to continue",
  hint,
  next,
}: {
  title?: string;
  hint?: string;
  next: string;
}) {
  return (
    <div className="mx-auto max-w-md animate-scale-in">
      <Card className="flex flex-col items-center gap-4 px-6 py-12 text-center">
        <DiamondMark className="h-12 w-12" />
        <div className="space-y-1">
          <h1 className="font-display text-xl font-bold tracking-tight text-navy-950">{title}</h1>
          {hint ? <p className="text-sm text-slate-500">{hint}</p> : null}
        </div>
        <ButtonLink size="lg" to={loginPath(next)} className="mt-2 w-full max-w-xs">
          Sign in
        </ButtonLink>
        <p className="text-sm text-slate-500">
          New here?{" "}
          <Link
            to={signupPath(next)}
            className="font-medium text-brandText underline-offset-2 transition-colors hover:text-brandText hover:underline"
          >
            Create an account
          </Link>
        </p>
      </Card>
    </div>
  );
}

/* ----------------------------------- marks ----------------------------------- */

/** Baseball-diamond logo mark. */
export function DiamondMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" className={className} aria-hidden>
      <rect width="32" height="32" rx="8" className="fill-brand-600" />
      <path
        d="M16 7 25 16 16 25 7 16Z"
        fill="none"
        stroke="white"
        strokeWidth="1.8"
        strokeLinejoin="round"
        className="opacity-90"
      />
      <circle cx="16" cy="16" r="2.1" fill="white" />
    </svg>
  );
}

/* ----------------------------------- icons ----------------------------------- */

interface IconProps {
  className?: string;
}

function iconAttrs(className: string | undefined) {
  return {
    className: className ?? "h-4 w-4",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  } as const;
}

export function CalendarIcon({ className }: IconProps) {
  return (
    <svg {...iconAttrs(className)}>
      <rect x="3" y="4.5" width="18" height="17" rx="2.5" />
      <path d="M3 9.5h18M8 2.5v4M16 2.5v4" />
    </svg>
  );
}

export function MapPinIcon({ className }: IconProps) {
  return (
    <svg {...iconAttrs(className)}>
      <path d="M12 21s-7-5.5-7-11a7 7 0 1 1 14 0c0 5.5-7 11-7 11Z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}

export function ArrowUpRightIcon({ className }: IconProps) {
  return (
    <svg {...iconAttrs(className)}>
      <path d="M7 17 17 7M9 7h8v8" />
    </svg>
  );
}

export function ChevronRightIcon({ className }: IconProps) {
  return (
    <svg {...iconAttrs(className)}>
      <path d="m9 5 7 7-7 7" />
    </svg>
  );
}

export function TrophyIcon({ className }: IconProps) {
  return (
    <svg {...iconAttrs(className)}>
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
      <path d="M4 22h16" />
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
    </svg>
  );
}

export function PlusIcon({ className }: IconProps) {
  return (
    <svg {...iconAttrs(className)}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

export function CheckCircleIcon({ className }: IconProps) {
  return (
    <svg {...iconAttrs(className)}>
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12.5 2.5 2.5 5-5.5" />
    </svg>
  );
}

export function SearchIcon({ className }: IconProps) {
  return (
    <svg {...iconAttrs(className)}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

export function XIcon({ className }: IconProps) {
  return (
    <svg {...iconAttrs(className)}>
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}

export function UploadIcon({ className }: IconProps) {
  return (
    <svg {...iconAttrs(className)}>
      <path d="M12 15.5V4m0 0L7.5 8.5M12 4l4.5 4.5" />
      <path d="M4 15v3.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V15" />
    </svg>
  );
}

export function TrashIcon({ className }: IconProps) {
  return (
    <svg {...iconAttrs(className)}>
      <path d="M4 7h16M10 11v6M14 11v6" />
      <path d="M6 7l1 13h10l1-13M9 7V4h6v3" />
    </svg>
  );
}

export function MailIcon({ className }: IconProps) {
  return (
    <svg {...iconAttrs(className)}>
      <rect x="2.5" y="4.5" width="19" height="15" rx="2.5" />
      <path d="m3.5 7 7.6 5.1a1.6 1.6 0 0 0 1.8 0L20.5 7" />
    </svg>
  );
}

export function PhoneIcon({ className }: IconProps) {
  return (
    <svg {...iconAttrs(className)}>
      <path d="M6.5 3.5h3l1.5 4-2 1.5a12 12 0 0 0 6 6l1.5-2 4 1.5v3a2 2 0 0 1-2.2 2A17 17 0 0 1 4.5 5.7a2 2 0 0 1 2-2.2Z" />
    </svg>
  );
}

/*
  The three social marks are simplified boxed glyphs in this file's stroke
  style, not official brand assets. Each renders beside its platform's name on
  the About page, so the label carries the meaning and the glyph only has to be
  recognizable.
*/
export function FacebookIcon({ className }: IconProps) {
  return (
    <svg {...iconAttrs(className)}>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <path d="M15.2 7.8h-1.6a1.9 1.9 0 0 0-1.9 1.9V20.5M9.6 13h5.2" />
    </svg>
  );
}

export function InstagramIcon({ className }: IconProps) {
  return (
    <svg {...iconAttrs(className)}>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="3.8" />
      <circle cx="17.1" cy="6.9" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** The social platform, boxed — `XIcon` above is the close glyph. */
export function XSocialIcon({ className }: IconProps) {
  return (
    <svg {...iconAttrs(className)}>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <path d="m8.4 8.4 7.2 7.2M15.6 8.4l-7.2 7.2" />
    </svg>
  );
}
